import { Accordion, Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { AccordionLabel, AccordionPanel, Divider } from '@care/react-component-lib';
import { SxClassProps } from '@/types';
import useResponsive from '@/components/hooks/useResponsive';
import { AnalyticsHelper } from '@/utilities/AnalyticsHelper';
import { AnalyticsEventName, CtaLocation, ScreenName } from '@/types/enums';

const classes: SxClassProps = {
  faqsBox: (theme) => ({
    display: 'flex',
    flexDirection: { xs: 'column', xl: 'row' },
    padding: {
      xs: theme.spacing(8, 3, 6, 3),
      sm: theme.spacing(8, 0, 6),
      lg: theme.spacing(1, 0, 6),
      xl: theme.spacing(8, 0, '207px'),
    },
    width: {
      sm: '501px',
      md: '737px',
      xl: '1280px',
    },
  }),
  header: {
    alignSelf: { xs: 'center', xl: 'flex-start' },
    textAlign: { xs: 'center', xl: 'left' },
    width: { xs: '250px', sm: '100%', xl: '411px' },
    mb: 3,
    margin: { xl: '0 132px 0 0' },
  },
  accordion: {
    display: 'flex',
    flexDirection: 'column',
    width: { xl: '737px' },
    '& .MuiAccordionSummary-content': {
      alignSelf: 'flex-start',
      my: 0,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      position: 'relative',
      alignSelf: 'flex-start',
      top: '3px',
    },
    '& .MuiButtonBase-root': {
      minHeight: 'fit-content',
    },
  },
  itemSummary: {
    '& .MuiTypography-root': {
      fontWeight: 'bold',
      fontSize: '18px',
      width: { xs: '291px', sm: '469px', md: '619px' },
    },
  },
  itemPanel: {
    padding: 0,
    mt: 0.5,
  },
  itemDescription: {
    lineHeight: '22px',
  },
};

type Item = {
  key: string;
  summary: string;
  description: string;
};

export const items: Item[] = [
  {
    key: 'a',
    summary: 'How do I pay the caregiver once hired?',
    description:
      'Payment terms are agreed upon between you and the caregiver. We also offer HomePay—an optional payroll service that helps you handle payments, taxes, and paperwork with ease.',
  },
  {
    key: 'b',
    summary: 'Are there additional costs beyond the subscription fee?',
    description:
      'It depends—families may choose to pay for optional services like background checks or payroll services. Since caregivers set their own rates, the cost of hiring a caregiver will vary. There are also booking fees if you decide to go with one-time booking.',
  },
  {
    key: 'c',
    summary: 'How do I cancel my subscription?',
    description:
      'You can cancel your membership at any time by visiting your account settings and selecting the cancellation option. Depending on your plan, your benefits will remain active until the end of your billing period.',
  },
  {
    key: 'd',
    summary: 'Do you offer refunds if I cancel my subscription early?',
    description:
      'Refund policies may vary—but typically, once a subscription fee is paid, it is non-refundable. Please refer to our cancellation and refund policy for more details.',
  },
];

const itemsKeys = items.reduce(
  (acc, item) => ({
    ...acc,
    [item.key]: false,
  }),
  {} as { [key: string]: boolean }
);

const FAQS = () => {
  const { isDesktopLG } = useResponsive();
  const [accordionState, setAccordionState] = useState<{
    [key: string]: boolean;
  }>(itemsKeys);

  const accordionChangeHandler = (key: string, summary: string) => {
    AnalyticsHelper.logEvent({
      name: AnalyticsEventName.CTA_INTERACTED,
      data: {
        screen_name: ScreenName.PLANS_AND_PRICING,
        cta_intent: !accordionState[key] ? 'open drawer' : 'close drawer',
        cta_text: !accordionState[key] ? '\u02C5' : '\u02C4',
        cta_location: CtaLocation.FAQS,
        enrollment_initiated: false,
        position: summary,
        vertical: 'Unknown',
      },
    });
    setAccordionState({ ...accordionState, [key]: !accordionState[key] });
  };

  return (
    <Box
      data-testid="faqs-section"
      sx={{
        display: 'grid',
        justifyItems: 'center',
      }}>
      <Box sx={classes.faqsBox}>
        <Typography sx={classes.header} variant={isDesktopLG ? 'display2' : 'h1'}>
          It&apos;s okay to have questions
        </Typography>

        <Box>
          <Divider sx={{ mb: 2, mt: 0 }} />

          {items.map(({ key, summary, description }) => (
            <Box key={key}>
              <Accordion
                sx={classes.accordion}
                elevation={0}
                expanded={accordionState[key]}
                onChange={() => {
                  accordionChangeHandler(key, summary);
                }}>
                <AccordionLabel sx={classes.itemSummary} primaryLabel={summary} />
                <AccordionPanel sx={classes.itemPanel}>
                  <Typography sx={classes.itemDescription} variant="body2">
                    {description}
                  </Typography>
                </AccordionPanel>
              </Accordion>

              <Divider sx={{ my: 2 }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default FAQS;
